/**
 * TermsConsentGate — sobrepõe o modal de aceite dos Termos de Uso (ATR-563).
 *
 * Diferente do `BillingGate`, este gate NÃO substitui a árvore de rotas: o CRM
 * continua montado por baixo do modal (que é bloqueante por conta própria).
 * Assim o app não remonta ao aceitar — o modal simplesmente some quando
 * `user.terms.pending` vira `false` no `/auth/me`.
 *
 * Só entra em cena para papéis de CRM autenticados em rota privada. MASTER e
 * ORG_OWNER operam o painel de gestão e estão fora do escopo v1 — o backend já
 * devolve `pending: false` para eles, e o gate reforça no cliente.
 */

import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth.store';
import { usePermissions } from '@/hooks/usePermissions';
import { TermsConsentModal } from './TermsConsentModal';

/** Rotas públicas de `App.tsx` — nunca recebem o modal. */
const PUBLIC_PATH_PREFIXES = [
  '/login',
  '/auth/magic',
  '/reset-password',
  '/opt-in',
] as const;

function isPublicPath(pathname: string): boolean {
  return PUBLIC_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

interface TermsConsentGateProps {
  children: ReactNode;
}

export function TermsConsentGate({ children }: TermsConsentGateProps) {
  const terms = useAuthStore((s) => s.user?.terms);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const isLoading = useAuthStore((s) => s.isLoading);
  const { pathname } = useLocation();
  const { isMaster, isOrgOwner } = usePermissions();

  // `isLoading` cobre a janela em que o `/auth/me` está em voo: o `user`
  // persistido no localStorage pode carregar um `terms` velho, e mostrar o modal
  // com base nele piscaria (ou reapareceria depois de um aceite feito em outra
  // aba). `terms` ausente (login, payload antigo) também não dispara nada.
  const shouldPrompt =
    isAuthenticated &&
    !isLoading &&
    !isMaster &&
    !isOrgOwner &&
    !isPublicPath(pathname) &&
    terms?.pending === true;

  return (
    <>
      {children}
      {shouldPrompt && terms ? <TermsConsentModal status={terms} /> : null}
    </>
  );
}
